import React, { useState, useEffect } from 'react';

const BRAND = 'linear-gradient(135deg, #7c2837 0%, #943143 50%, #a8384e 100%)';

export default function EventPublicRegister({ eventId, eventTitle }) {
  const [event, setEvent]       = useState(null);
  const [loading, setLoading]   = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone]         = useState(null);  // { status, message, new_account, event_date }
  const [error, setError]       = useState('');
  const [form, setForm]         = useState({ first_name: '', last_name: '', email: '', phone: '', image_rights_accepted: false, rules_accepted: false });

  useEffect(() => {
    fetch(`/api/v1/events/${eventId}`)
      .then(r => r.json())
      .then(d => { setEvent(d.event || d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [eventId]);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.image_rights_accepted || !form.rules_accepted) {
      setError('Vous devez accepter le droit à l\'image et le règlement intérieur');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const res = await fetch(`/api/v1/events/${eventId}/register_guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (data.success) {
        setDone(data);
      } else {
        setError(data.message || 'Une erreur est survenue');
      }
    } catch {
      setError('Erreur réseau, veuillez réessayer');
    } finally {
      setSubmitting(false);
    }
  };

  const pageStyle = {
    minHeight: '100vh',
    background: '#f7eef2',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '40px 16px',
  };

  const cardStyle = {
    background: '#ffffff',
    borderRadius: '16px',
    boxShadow: '0 4px 24px rgba(124,40,55,0.10)',
    border: '1px solid rgba(124,40,55,0.12)',
    width: '100%',
    maxWidth: '480px',
    overflow: 'hidden',
  };

  const headerStyle = {
    background: BRAND,
    padding: '32px',
    color: '#fff',
    textAlign: 'center',
  };

  const bodyStyle = { padding: '32px' };

  const inputStyle = {
    width: '100%',
    padding: '11px 14px',
    border: '1.5px solid rgba(124,40,55,0.18)',
    borderRadius: '8px',
    background: '#fdf6f8',
    color: '#2c0e16',
    fontSize: '15px',
    boxSizing: 'border-box',
    outline: 'none',
  };

  const labelStyle = {
    display: 'block',
    fontSize: '13px',
    fontWeight: 600,
    color: '#7a4050',
    marginBottom: '6px',
  };

  const btnStyle = {
    width: '100%',
    padding: '14px',
    background: BRAND,
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: 700,
    cursor: submitting ? 'not-allowed' : 'pointer',
    opacity: submitting ? 0.7 : 1,
    marginTop: '8px',
  };

  if (loading) {
    return (
      <div className="event-mobile-shell event-public-register-page" style={pageStyle}>
        <div style={cardStyle}>
          <div className="event-public-register-card-header" style={headerStyle}>
            <h1 style={{ margin: 0, fontSize: '22px' }}>Chargement…</h1>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="event-mobile-shell event-public-register-page" style={pageStyle}>
        <div style={cardStyle}>
          <div className="event-public-register-card-header" style={headerStyle}>
            <div style={{ fontSize: '40px', marginBottom: '8px' }}>🎉</div>
            <h1 style={{ margin: '0 0 6px', fontSize: '22px' }}>Inscription confirmée !</h1>
            <p style={{ margin: 0, opacity: 0.9 }}>{done.event_title}</p>
          </div>
          <div className="event-public-register-card-body" style={bodyStyle}>
            <p style={{ color: '#2c0e16', marginBottom: '12px' }}>
              {done.status === 'waiting' ? '⏳ Vous êtes sur liste d\'attente.' : '✅ Votre inscription est confirmée.'}
            </p>
            {done.event_date && (
              <p style={{ color: '#7a4050', marginBottom: '12px' }}>📅 {done.event_date}</p>
            )}
            {done.new_account && (
              <div style={{ background: '#f7eef2', border: '1px solid rgba(124,40,55,0.2)', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                <p style={{ margin: '0 0 8px', fontWeight: 700, color: '#7c2837', fontSize: '14px' }}>🔑 Un compte a été créé pour vous</p>
                <p style={{ margin: 0, fontSize: '13px', color: '#7a4050' }}>
                  Vérifiez vos emails — vous avez reçu vos identifiants de connexion.
                </p>
              </div>
            )}
            {event?.allow_plus_one && done.status !== 'waiting' && (
              <div style={{ background: '#fdf0f2', border: '1px solid rgba(124,40,55,0.2)', borderRadius: '10px', padding: '16px', marginBottom: '16px' }}>
                <p style={{ margin: '0 0 4px', fontWeight: 700, color: '#7c2837', fontSize: '14px' }}>👤 Vous pouvez ajouter un invité +1</p>
                <p style={{ margin: 0, fontSize: '13px', color: '#7a4050' }}>
                  Connectez-vous sur votre espace pour ajouter un invité sans compte — le billet sera à votre charge.
                </p>
              </div>
            )}
            <a
              href="/connexion"
              style={{ display: 'block', textAlign: 'center', ...btnStyle, textDecoration: 'none', boxSizing: 'border-box' }}
            >
              Se connecter
            </a>
          </div>
        </div>
      </div>
    );
  }

  const title = event?.title || eventTitle || 'Événement';
  const location = event?.location;
  const startDate = event?.start_date ? new Date(event.start_date).toLocaleString('fr-FR', { dateStyle: 'long', timeStyle: 'short' }) : null;

  return (
    <div className="event-mobile-shell event-public-register-page" style={pageStyle}>
      <div style={cardStyle}>
        <div className="event-public-register-card-header" style={headerStyle}>
          <h1 style={{ margin: '0 0 6px', fontSize: '22px' }}>Inscription</h1>
          <p style={{ margin: 0, opacity: 0.9, fontSize: '16px', fontWeight: 600 }}>{title}</p>
          {startDate && <p style={{ margin: '6px 0 0', opacity: 0.8, fontSize: '13px' }}>📅 {startDate}</p>}
          {location && <p style={{ margin: '4px 0 0', opacity: 0.8, fontSize: '13px' }}>📍 {location}</p>}
        </div>
        <div className="event-public-register-card-body" style={bodyStyle}>
          <p style={{ color: '#7a4050', marginBottom: '24px', fontSize: '14px' }}>
            Remplissez ce formulaire pour vous inscrire. Vous recevrez un email de confirmation avec vos identifiants de connexion.
          </p>

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Prénom *</label>
              <input
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                placeholder="Jean"
                required
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: '16px' }}>
              <label style={labelStyle}>Nom *</label>
              <input
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                placeholder="Dupont"
                required
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Email *</label>
              <input
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                placeholder="jean.dupont@exemple.fr"
                required
                style={inputStyle}
              />
            </div>
            <div style={{ marginBottom: '20px' }}>
              <label style={labelStyle}>Téléphone (optionnel)</label>
              <input
                name="phone"
                type="tel"
                value={form.phone}
                onChange={handleChange}
                placeholder="+33 6 12 34 56 78"
                style={inputStyle}
              />
              <div style={{ fontSize: '12px', color: '#9e6575', marginTop: '4px' }}>
                Pour recevoir d'éventuels rappels et infos par SMS.
              </div>
            </div>

            {/* Cases à cocher légales */}
            <div style={{ background: '#f8f0f2', borderRadius: '10px', border: '1px solid rgba(124,40,55,0.15)', padding: '16px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', marginBottom: '14px' }}>
                <input
                  type="checkbox"
                  id="imageRightsPublic"
                  checked={form.image_rights_accepted}
                  onChange={e => setForm(f => ({ ...f, image_rights_accepted: e.target.checked }))}
                  style={{ marginTop: '3px', accentColor: '#7c2837' }}
                />
                <label htmlFor="imageRightsPublic" style={{ fontSize: '13px', color: '#2c0e16', cursor: 'pointer' }}>
                  J'accepte le <a href="/mentions-legales/droit-image" target="_blank" rel="noopener noreferrer" style={{ color: '#7c2837', fontWeight: 600 }}>droit à l'image</a> <span style={{ color: '#dc3545' }}>*</span>
                </label>
              </div>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                <input
                  type="checkbox"
                  id="eventRulesPublic"
                  checked={form.rules_accepted}
                  onChange={e => setForm(f => ({ ...f, rules_accepted: e.target.checked }))}
                  style={{ marginTop: '3px', accentColor: '#7c2837' }}
                />
                <label htmlFor="eventRulesPublic" style={{ fontSize: '13px', color: '#2c0e16', cursor: 'pointer' }}>
                  J'accepte le <a href="/mentions-legales/reglement-interieur" target="_blank" rel="noopener noreferrer" style={{ color: '#7c2837', fontWeight: 600 }}>règlement intérieur des événements</a> <span style={{ color: '#dc3545' }}>*</span>
                </label>
              </div>
            </div>

            {error && (
              <div style={{ background: '#fdf0f2', border: '1px solid rgba(168,56,78,0.3)', borderRadius: '8px', padding: '12px', marginBottom: '16px', color: '#7c2837', fontSize: '14px' }}>
                {error}
              </div>
            )}

            <button type="submit" style={btnStyle} disabled={submitting}>
              {submitting ? 'Inscription en cours…' : "S'inscrire à l'événement"}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '13px', color: '#9e6575' }}>
            Vous avez déjà un compte ?{' '}
            <a href="/connexion" style={{ color: '#7c2837', fontWeight: 600 }}>Se connecter</a>
          </p>
        </div>
      </div>
    </div>
  );
}
